import React, { useState, useEffect } from 'react';
import { Edit2, Trash2, Plus } from 'lucide-react';
import api from '../services/api';
import ConfirmModal from './ConfirmModal';
import { showToast } from '../utils/toast';
import '../styles/AdminComponents.css';

// Paleta sugerida — clicáveis no formulário; usuário pode também escolher qualquer cor.
const PRESET_COLORS = [
  '#4F46E5', '#16A34A', '#DC2626', '#D97706', '#0891B2', '#DB2777',
  '#7C3AED', '#0284C7', '#65A30D', '#EA580C', '#0F766E', '#9333EA'
];

function DepartmentManagement() {
  const [departments, setDepartments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '', color: '#4F46E5' });
  const [loading, setLoading] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => { fetchDepartments(); }, []);

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('Erro ao buscar departamentos:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (formData.id) {
        await api.put(`/departments/${formData.id}`, formData);
        showToast('Departamento atualizado!', 'success');
      } else {
        await api.post('/departments', formData);
        showToast('Departamento criado!', 'success');
      }
      setFormData({ name: '', description: '', color: '#4F46E5' });
      setShowForm(false);
      fetchDepartments();
    } catch (error) {
      showToast(error.response?.data?.message || 'Erro ao salvar departamento', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (dept) => {
    setFormData({
      id: dept.id,
      name: dept.name,
      description: dept.description || '',
      color: dept.color || '#4F46E5'
    });
    setShowForm(true);
  };

  const doDelete = async () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    try {
      await api.delete(`/departments/${id}`);
      showToast('Departamento excluído', 'success');
      fetchDepartments();
    } catch (error) {
      showToast(error.response?.data?.message || 'Erro ao excluir departamento', 'error');
    }
  };

  return (
    <div className="admin-component">
      <div className="admin-header">
        <h2>Gerenciar Departamentos</h2>
        <button onClick={() => { setFormData({ name: '', description: '', color: '#4F46E5' }); setShowForm(true); }} className="add-btn">
          <Plus size={20} />
          Novo Departamento
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="admin-form">
          <input
            type="text"
            placeholder="Nome do departamento"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <textarea
            placeholder="Descrição"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows="3"
          />

          <div className="dept-color-picker">
            <label>Cor do departamento</label>
            <div className="dept-color-row">
              {PRESET_COLORS.map(c => (
                <button
                  key={c}
                  type="button"
                  title={c}
                  className={`dept-color-swatch${formData.color === c ? ' selected' : ''}`}
                  style={{ background: c }}
                  onClick={() => setFormData({ ...formData, color: c })}
                />
              ))}
              <input
                type="color"
                value={formData.color || '#4F46E5'}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                title="Escolher cor personalizada"
                className="dept-color-input"
              />
              <span className="dept-color-preview" style={{ background: formData.color }}>
                {formData.name || 'Prévia'}
              </span>
            </div>
          </div>

          <button type="submit" disabled={loading}>
            {loading ? 'Salvando...' : 'Salvar'}
          </button>
          <button type="button" onClick={() => { setShowForm(false); setFormData({ name: '', description: '', color: '#4F46E5' }); }}>
            Cancelar
          </button>
        </form>
      )}

      <div className="departments-table">
        <table>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Descrição</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {departments.map(dept => (
              <tr key={dept.id}>
                <td>
                  <span className="dept-name-badge" style={{ background: dept.color || '#4F46E5' }}>
                    {dept.name}
                  </span>
                </td>
                <td>{dept.description || '-'}</td>
                <td className="actions">
                  <button className="edit-btn" onClick={() => handleEdit(dept)} title="Editar">
                    <Edit2 size={18} />
                  </button>
                  <button className="delete-btn" onClick={() => setConfirmDeleteId(dept.id)} title="Excluir">
                    <Trash2 size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {confirmDeleteId && (
        <ConfirmModal
          title="Excluir departamento"
          message="Tem certeza? Conversas já vinculadas a este departamento não serão afetadas, mas ele deixará de aparecer nos menus."
          confirmLabel="Excluir"
          onConfirm={doDelete}
          onCancel={() => setConfirmDeleteId(null)}
          danger
        />
      )}
    </div>
  );
}

export default DepartmentManagement;
